/**
 * Admin -> Mail & Notifications.
 *
 * Edits the runtime settings that live in `dbo.app_config` (SMTP transport +
 * notifier behaviour), so the SES credentials and the manual-checkout email
 * toggles can be changed from the website instead of by hand-running SQL. The
 * notifier re-reads app_config every cycle, so a save takes effect on the next
 * cycle with no restart and no deploy.
 *
 * Two behaviours worth knowing before editing this file:
 *
 *  - A SECRET field never arrives from the server (only `is_set`). Its input
 *    starts blank and an untouched blank is NOT submitted, so saving an
 *    unrelated field cannot wipe the stored password. That is enforced on the
 *    server too, so a hand-rolled request can't wipe it either.
 *  - `is_set` is false for a `REPLACE_ME` placeholder, matching how the notifier
 *    treats it. Otherwise this screen would claim mail was configured while the
 *    notifier was silently skipping every send.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "../ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { errMsg } from "../../lib/errMsg";
import {
  type AdminSetting,
  type TestEmailResult,
  listSettings,
  sendTestEmail,
  updateSettings,
} from "../../services/settingsAdminApi";

const SECTION_LABEL: Record<string, string> = {
  MttrReportSettings: "Mail transport (SMTP)",
  MttrNotifierSettings: "Notifier",
  MttrSettings: "Report delivery",
};

export const MailSettingsTab: React.FC = () => {
  const { token } = useAuth();

  const [settings, setSettings] = useState<AdminSetting[]>([]);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<TestEmailResult | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      // Mail tab renders only category='mail'; operational toggles/thresholds
      // live on the System tab (same API, filtered by category).
      const rows = (await listSettings(token)).filter((s) => s.category === "mail");
      setSettings(rows);
      // Secrets start blank on purpose (see file header).
      const d: Record<string, string> = {};
      rows.forEach((s) => {
        d[s.id] = s.secret ? "" : (s.value ?? "");
      });
      setDraft(d);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const out: Record<string, AdminSetting[]> = {};
    settings.forEach((s) => {
      (out[s.section] ||= []).push(s);
    });
    return out;
  }, [settings]);

  /** Only fields the user actually changed; blank secrets are never sent. */
  const changes = useMemo(() => {
    const out: Record<string, string> = {};
    settings.forEach((s) => {
      const next = draft[s.id] ?? "";
      if (s.secret) {
        if (next !== "") out[s.id] = next;
        return;
      }
      if (next !== (s.value ?? "")) out[s.id] = next;
    });
    return out;
  }, [settings, draft]);

  const dirtyCount = Object.keys(changes).length;

  const save = async () => {
    if (!token || dirtyCount === 0) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const res = await updateSettings(token, changes);
      setNotice(
        `Saved ${res.updated.length} setting${res.updated.length === 1 ? "" : "s"}.` +
          (res.unchanged.length
            ? ` ${res.unchanged.length} left unchanged (blank secret).`
            : ""),
      );
      await load(); // re-read so is_set / values reflect what's stored
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!token) return;
    setTesting(true);
    setTestResult(null);
    setError(null);
    try {
      setTestResult(await sendTestEmail(token));
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setTesting(false);
    }
  };

  const field = (s: AdminSetting) => {
    const value = draft[s.id] ?? "";
    const set = (v: string) => setDraft((p) => ({ ...p, [s.id]: v }));

    if (s.value_type === "bool") {
      return (
        <select
          className="border border-secondary-300 rounded px-2 py-1 text-sm w-full max-w-xs"
          value={value === "" ? "false" : value}
          onChange={(e) => set(e.target.value)}
        >
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </select>
      );
    }
    return (
      <input
        type={s.secret ? "password" : "text"}
        className="border border-secondary-300 rounded px-2 py-1 text-sm w-full max-w-md font-mono"
        value={value}
        inputMode={s.value_type === "int" ? "numeric" : undefined}
        placeholder={
          s.secret
            ? s.is_set
              ? "•••••  (stored - leave blank to keep)"
              : "not set"
            : ""
        }
        autoComplete={s.secret ? "new-password" : "off"}
        onChange={(e) => set(e.target.value)}
      />
    );
  };

  return (
    <div className="space-y-6">
      {error && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="text-sm text-green-800 bg-green-50 border border-green-200 rounded px-3 py-2">
          {notice}
        </div>
      )}

      {loading && <div className="text-sm text-secondary-500">Loading…</div>}

      {Object.entries(grouped).map(([section, rows]) => (
        <section key={section}>
          <h2 className="text-sm font-semibold mb-2">
            {SECTION_LABEL[section] ?? section}
          </h2>
          <div className="space-y-3">
            {rows.map((s) => (
              <div key={s.id} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-3">
                <label className="text-sm text-secondary-700 md:w-64 shrink-0">
                  {s.label}
                  {s.secret && (
                    <span
                      className={
                        "ml-2 text-[10px] px-1 py-0.5 rounded " +
                        (s.is_set
                          ? "bg-green-100 text-green-800"
                          : "bg-amber-100 text-amber-800")
                      }
                      title={
                        s.is_set
                          ? "A value is stored. It is never shown here."
                          : "Not configured - the notifier will skip sends until this is set."
                      }
                    >
                      {s.is_set ? "set" : "not set"}
                    </span>
                  )}
                </label>
                <div className="flex-1">
                  {field(s)}
                  {s.help && (
                    <div className="text-[11px] text-secondary-500 mt-0.5">{s.help}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-secondary-200">
        <Button onClick={save} disabled={saving || dirtyCount === 0}>
          {saving ? "Saving…" : dirtyCount ? `Save ${dirtyCount} change${dirtyCount === 1 ? "" : "s"}` : "Save"}
        </Button>
        <Button variant="outline" onClick={runTest} disabled={testing}>
          {testing ? "Sending…" : "Send test email to me"}
        </Button>
        {dirtyCount > 0 && (
          <span className="text-xs text-amber-700">Unsaved changes</span>
        )}
      </div>

      {testResult && (
        <div
          className={
            "text-sm rounded px-3 py-2 border " +
            (testResult.delivered
              ? "text-green-800 bg-green-50 border-green-200"
              : "text-red-700 bg-red-50 border-red-200")
          }
        >
          {testResult.delivered
            ? `Test email sent to ${testResult.to} via ${testResult.via}.`
            : `Test failed via ${testResult.via}: ${testResult.detail}`}
          {/* The raw SMTP error is what makes a bad credential diagnosable
              here rather than "it didn't work". */}
        </div>
      )}

      <p className="text-[11px] text-secondary-500">
        Stored in <code>app_config</code>. The notifier re-reads these every
        cycle, so changes apply without a restart. Passwords are write-only —
        they are never sent back to this screen.
      </p>
    </div>
  );
};

export default MailSettingsTab;
